import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Tag } from "lucide-react";
import {
  listarTiposProjeto,
  criarTipoProjeto,
  atualizarTipoProjeto,
  excluirTipoProjeto,
  TipoProjeto,
} from "../services/tiposProjeto";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

export function TiposProjeto() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  const ehAdmin = can(["admin"]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: tipos = [], isLoading } = useQuery({
    queryKey: ["tipos-projeto", "todos"],
    queryFn: () => listarTiposProjeto({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: criarTipoProjeto,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tipos-projeto"] });
      setNovo(false);
    },
  });

  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarTipoProjeto(id, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tipos-projeto"] });
      setEditandoId(null);
    },
  });

  const excluirM = useMutation({
    mutationFn: excluirTipoProjeto,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tipos-projeto"] }),
    onError: (e: any) => {
      const msg = e?.response?.data?.error?.message || "Erro ao excluir";
      confirm({ title: "Não foi possível excluir", message: msg, confirmText: "OK" });
    },
  });

  async function aoExcluir(t: TipoProjeto) {
    const ok = await confirm({
      title: `Excluir "${t.label}"?`,
      message: "Se houver projetos usando esse tipo, a exclusão será bloqueada — desative em vez disso.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(t.id);
  }

  if (!can(["gestor", "admin"])) return <div className="text-slate-500">Acesso restrito.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Tag size={20} /> Tipos de projeto
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Categorias usadas em projetos e nos filtros de tarefas. Desativar um tipo o esconde de novos projetos sem afetar projetos antigos.
          </p>
        </div>
        <button
          onClick={() => setNovo(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Novo tipo
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Cor</th>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-left px-4 py-2 font-medium">Slug</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && (
              <LinhaEditavel
                tipo={null}
                onSalvar={(dados) => criarM.mutate(dados)}
                onCancelar={() => setNovo(false)}
                salvando={criarM.isPending}
              />
            )}
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">Carregando…</td>
              </tr>
            )}
            {!isLoading && tipos.length === 0 && !novo && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nenhum tipo cadastrado.</td>
              </tr>
            )}
            {tipos.map((t) =>
              editandoId === t.id ? (
                <LinhaEditavel
                  key={t.id}
                  tipo={t}
                  onSalvar={(dados) => atualizarM.mutate({ id: t.id, dados })}
                  onCancelar={() => setEditandoId(null)}
                  salvando={atualizarM.isPending}
                />
              ) : (
                <tr key={t.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                      style={{ background: t.cor || "#cbd5e1", color: "#fff" }}
                    >
                      <span className="text-base leading-none">{t.icone}</span>
                      <span>{t.label}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium">{t.label}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{t.slug}</td>
                  <td className="px-4 py-2 tabular-nums">{t.ordem}</td>
                  <td className="px-4 py-2">
                    {t.ativo ? (
                      <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativo</span>
                    ) : (
                      <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativo</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => atualizarM.mutate({ id: t.id, dados: { ativo: !t.ativo } })}
                      className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                      title={t.ativo ? "Desativar" : "Ativar"}
                    >
                      {t.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      onClick={() => setEditandoId(t.id)}
                      className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                      title="Editar"
                    >
                      <Pencil size={14} />
                    </button>
                    {ehAdmin && (
                      <button
                        onClick={() => aoExcluir(t)}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        title="Excluir"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaEditavel({ tipo, onSalvar, onCancelar, salvando }: {
  tipo: TipoProjeto | null;
  onSalvar: (dados: { label: string; slug?: string; cor: string | null; icone: string; ordem: number; ativo: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [label, setLabel] = useState(tipo?.label || "");
  const [cor, setCor] = useState(tipo?.cor || "#64748b");
  const [icone, setIcone] = useState(tipo?.icone || "📁");
  const [ordem, setOrdem] = useState(tipo?.ordem ?? 0);
  const [ativo, setAtivo] = useState(tipo?.ativo ?? true);

  function salvar() {
    if (!label.trim()) return;
    onSalvar({ label: label.trim(), cor, icone: icone || "📁", ordem: Number(ordem) || 0, ativo });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <div className="flex items-center gap-1.5">
          <input
            type="color"
            value={cor || "#64748b"}
            onChange={(e) => setCor(e.target.value)}
            className="w-8 h-7 rounded cursor-pointer border border-slate-300"
            title="Cor"
          />
          <input
            type="text"
            value={icone}
            onChange={(e) => setIcone(e.target.value)}
            placeholder="📁"
            maxLength={4}
            className="w-12 h-7 rounded text-center border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-base"
            title="Ícone (emoji)"
          />
        </div>
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex.: Design"
          className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
          autoFocus
        />
      </td>
      <td className="px-4 py-2 font-mono text-xs text-slate-400">
        {tipo ? tipo.slug : <em>(gerado automaticamente)</em>}
      </td>
      <td className="px-4 py-2">
        <input
          type="number"
          value={ordem}
          onChange={(e) => setOrdem(Number(e.target.value) || 0)}
          className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
        />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          Ativo
        </label>
      </td>
      <td className="px-4 py-2 text-right">
        <button
          onClick={salvar}
          disabled={!label.trim() || salvando}
          className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40"
          title="Salvar"
        >
          <Check size={14} />
        </button>
        <button
          onClick={onCancelar}
          className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
          title="Cancelar"
        >
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}
